/**
 * TOAST NOTIFICATIONS
 *
 * Utility functions for showing toast notifications throughout the app.
 * Uses react-hot-toast for lightweight, customizable notifications.
 *
 * Usage:
 *   import { showSuccess, showError, showInfo } from '../utils/toast';
 *
 *   showSuccess('Message sent!');
 *   showError('Failed to save');
 *   showInfo('Processing...');
 */

import toast from 'react-hot-toast';

// Default duration in milliseconds
const DEFAULT_DURATION = 3000;
const ERROR_DURATION = 4000;

/**
 * Show a success toast (green checkmark)
 */
export const showSuccess = (message: string, duration = DEFAULT_DURATION) => {
  toast.success(message, {
    duration,
    style: {
      background: '#10B981',
      color: '#fff',
      fontFamily: 'Nunito, sans-serif',
      fontWeight: 600,
    },
    iconTheme: {
      primary: '#fff',
      secondary: '#10B981',
    },
  });
};

/**
 * Show an error toast (red X)
 */
export const showError = (message: string, duration = ERROR_DURATION) => {
  toast.error(message, {
    duration,
    style: {
      background: '#EF4444',
      color: '#fff',
      fontFamily: 'Nunito, sans-serif',
      fontWeight: 600,
    },
    iconTheme: {
      primary: '#fff',
      secondary: '#EF4444',
    },
  });
};

/**
 * Show an info toast (blue info icon)
 */
export const showInfo = (message: string, duration = DEFAULT_DURATION) => {
  toast(message, {
    duration,
    icon: 'ℹ️',
    style: {
      background: '#3B82F6',
      color: '#fff',
      fontFamily: 'Nunito, sans-serif',
      fontWeight: 600,
    },
  });
};

/**
 * Show a loading toast that can be updated later
 * Returns a toast ID that can be used to dismiss or update
 */
export const showLoading = (message: string) => {
  return toast.loading(message, {
    style: {
      background: '#1E293B',
      color: '#fff',
      fontFamily: 'Nunito, sans-serif',
      fontWeight: 600,
    },
  });
};

/**
 * Dismiss a specific toast by ID
 */
export const dismissToast = (toastId: string) => {
  toast.dismiss(toastId);
};

/**
 * Dismiss all toasts
 */
export const dismissAllToasts = () => {
  toast.dismiss();
};

/**
 * Show a promise toast that updates based on promise state
 * Great for async operations like saving, uploading, etc.
 *
 * Usage:
 *   showPromise(
 *     saveData(),
 *     { loading: 'Saving...', success: 'Saved!', error: 'Failed to save' }
 *   );
 */
export const showPromise = <T>(
  promise: Promise<T>,
  messages: { loading: string; success: string; error: string }
) => {
  return toast.promise(promise, messages, {
    style: {
      fontFamily: 'Nunito, sans-serif',
      fontWeight: 600,
    },
    success: {
      style: {
        background: '#10B981',
        color: '#fff',
      },
    },
    error: {
      style: {
        background: '#EF4444',
        color: '#fff',
      },
    },
  });
};
